// SPDX-License-Identifier: BUSL-1.1

/**
 * TenantSettingsForm — cloud provider connection form.
 *
 * Allows tenant to:
 * - Select a cloud provider (Hetzner, AWS, CF Containers)
 * - Enter and save an API token
 * - See a masked token confirmation on save
 * - Clear credentials (drain flow)
 *
 */

import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiFetch } from '@/lib/api-client';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { showSuccess, showApiError } from '@/lib/toast';

interface TenantSettings {
  provider: string | null;
  tokenMasked: string | null;
}

export function TenantSettingsForm() {
  const qc = useQueryClient();
  const [provider, setProvider] = useState<string>('');
  const [token, setToken] = useState('');
  const [showToken, setShowToken] = useState(false);
  const [hasSaved, setHasSaved] = useState(false);

  const { data, isLoading } = useQuery({
    queryKey: ['tenant-settings'],
    queryFn: () => apiFetch<TenantSettings>('/tenant/settings'),
  });

  // Pre-fill if settings already exist
  const existingProvider = data?.provider ?? null;

  const saveMutation = useMutation({
    mutationFn: () =>
      apiFetch<TenantSettings>('/tenant/settings', {
        method: 'PUT',
        body: JSON.stringify({ provider, token }),
      }),
    onSuccess: () => {
      showSuccess('Cloud provider saved');
      setHasSaved(true);
      setToken('');
      void qc.invalidateQueries({ queryKey: ['tenant-settings'] });
    },
    onError: showApiError,
  });

  const clearMutation = useMutation({
    mutationFn: () =>
      apiFetch<{ ok: boolean }>('/tenant/settings', { method: 'DELETE' }),
    onSuccess: () => {
      showSuccess('Cloud provider disconnected');
      setHasSaved(false);
      setProvider('');
      setToken('');
      void qc.invalidateQueries({ queryKey: ['tenant-settings'] });
    },
    onError: showApiError,
  });

  if (isLoading) {
    return (
      <div className="flex flex-col gap-4">
        <div className="h-5 w-32 animate-pulse rounded bg-neutral-800" />
        <div className="h-10 w-full animate-pulse rounded-lg bg-neutral-800" />
        <div className="h-5 w-28 animate-pulse rounded bg-neutral-800" />
        <div className="h-10 w-full animate-pulse rounded-lg bg-neutral-800" />
      </div>
    );
  }

  const hasCredentials = existingProvider !== null;

  return (
    <div className="flex flex-col gap-6">
      {/* Provider selection */}
      <div className="flex flex-col gap-1.5">
        <Label htmlFor="provider">Cloud Provider</Label>
        <Select
          value={provider || existingProvider || ''}
          onValueChange={(v) => {
            if (v) setProvider(v);
          }}
        >
          <SelectTrigger id="provider" className="w-full">
            <SelectValue placeholder="Select a provider" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="hetzner">Hetzner Cloud</SelectItem>
            <SelectItem value="aws" disabled>Amazon Web Services (coming soon)</SelectItem>
            <SelectItem value="cf-containers" disabled>Cloudflare Containers (coming soon)</SelectItem>
          </SelectContent>
        </Select>
        <p className="text-xs text-neutral-500">
          Hetzner is the first provider available in Phase 1.
        </p>
      </div>

      {/* Token input */}
      <div className="flex flex-col gap-1.5">
        <Label htmlFor="token">
          {hasCredentials ? 'Update API Token' : 'API Token'}
        </Label>
        <div className="relative">
          <Input
            id="token"
            type={showToken ? 'text' : 'password'}
            value={token}
            onChange={(e) => setToken(e.target.value)}
            placeholder={hasCredentials ? '••••••••••••' : 'hcn_...'}
            className="pr-10"
            autoComplete="off"
            spellCheck={false}
          />
          <button
            type="button"
            onClick={() => setShowToken((s) => !s)}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-neutral-500 hover:text-neutral-300"
          >
            {showToken ? 'Hide' : 'Show'}
          </button>
        </div>
        <p className="text-xs text-neutral-500">
          Create a Hetzner API token at{' '}
          <span className="font-mono text-neutral-400">console.hetzner.cloud → Project → Access → API Tokens → Create</span>.
          Give it <strong>Read &amp; Write</strong> permissions for Servers.
        </p>
      </div>

      {/* Actions */}
      <div className="flex items-center gap-3">
        <Button
          variant="default"
          disabled={
            !provider ||
            !token ||
            saveMutation.isPending ||
            clearMutation.isPending
          }
          onClick={() => saveMutation.mutate()}
        >
          {saveMutation.isPending ? 'Saving...' : 'Save'}
        </Button>

        {hasCredentials && (
          <Button
            variant="ghost"
            className="text-red-400 hover:text-red-300 hover:bg-red-950/30"
            disabled={saveMutation.isPending || clearMutation.isPending}
            onClick={() => clearMutation.mutate()}
          >
            {clearMutation.isPending ? 'Disconnecting...' : 'Disconnect'}
          </Button>
        )}
      </div>

      {/* Confirmation message */}
      {hasSaved && !hasCredentials && (
        <p className="text-sm text-green-400">
          Token saved — it will be encrypted before storage.
        </p>
      )}
    </div>
  );
}